import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, BACKEND_URL, getToken, fileToBase64 } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { ADMIN } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CountUp } from "@/components/Motion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, Check, X, Megaphone, Link as LinkIcon, Calendar, Plus, Trash2, AlertTriangle, CheckCircle2, Upload, BookOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [exhibitors, setExhibitors] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [magicLinks, setMagicLinks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/"); return; }
    Promise.all([
      api.get("/admin/stats").then(r => setStats(r.data)),
      api.get("/admin/exhibitors").then(r => setExhibitors(r.data.exhibitors)),
      api.get("/announcements").then(r => setAnnouncements(r.data.announcements)),
      api.get("/admin/magic-links").then(r => setMagicLinks(r.data.magic_links)),
      api.get("/sessions").then(r => setSessions(r.data.sessions)),
      api.get("/admin/procurement/meetings").then(r => setMeetings(r.data.meetings)),
      api.get("/admin/readiness").then(r => setReadiness(r.data)),
    ]).catch(() => {});
  }, [user, loading]);

  if (loading) return (<Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>);
  if (!user || (user.role !== "admin" && user.role !== "tl_internal")) return null;

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/exhibitors/${id}/status`, { status });
      setExhibitors((es) => es.map((e) => e.id === id ? data.exhibitor : e));
      toast.success(`Exhibitor ${status}.`);
    } catch (e) { toast.error("Update failed"); }
  };

  const togglePayment = async (e) => {
    const next = e.payment_status === "paid" ? "unpaid" : "paid";
    try {
      const { data } = await api.put(`/admin/exhibitors/${e.id}/payment`, { payment_status: next });
      setExhibitors((es) => es.map((x) => x.id === e.id ? data.exhibitor : x));
      toast.success(`Payment marked ${next}.`);
    } catch (er) { toast.error("Update failed"); }
  };

  const setBooth = async (id, value) => {
    try {
      const { data } = await api.put(`/admin/exhibitors/${id}/status`, { status: exhibitors.find(e => e.id === id).status, booth_number: value });
      setExhibitors((es) => es.map((e) => e.id === id ? data.exhibitor : e));
    } catch (er) { toast.error("Update failed"); }
  };

  const exportCsv = () => {
    fetch(`${BACKEND_URL}/api/admin/export.csv`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "exhibitors.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <Layout>
      <section className="container-summit py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <div className="overline mb-2"><span className="accent-rule" />Admin Dashboard</div>
            <h1 className="text-4xl">Summit Operations</h1>
          </div>
          <button onClick={exportCsv} className="btn-outline-black" data-testid={ADMIN.exportCsv}>
            <Download size={16} /> Export CSV
          </button>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          <Link to="/command-center" className="btn-black h-10 text-xs" data-testid="admin-link-command-center">Command Center →</Link>
          <Link to="/checkin" className="btn-outline-black h-10 text-xs" data-testid="admin-link-checkin">Event-Day Check-In →</Link>
          <Link to="/event-settings" className="btn-outline-black h-10 text-xs" data-testid="admin-link-event-settings">Event Settings →</Link>
          <Link to="/executive" className="btn-outline-black h-10 text-xs" data-testid="admin-link-executive">Executive Dashboard →</Link>
          <Link to="/operations" className="btn-outline-black h-10 text-xs" data-testid="admin-link-operations">Ops Tickets →</Link>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-[hsl(var(--line))] mb-10">
            <Stat label="Exhibitors" value={stats.exhibitors} />
            <Stat label="Approved" value={stats.approved} />
            <Stat label="Pending" value={stats.pending} />
            <Stat label="Paid" value={stats.paid} />
            <Stat label="Missing logos" value={stats.missing_logos} />
            <Stat label="Attendees" value={stats.attendees} />
            <Stat label="Meetings" value={stats.procurement_meetings} />
          </div>
        )}

        {readiness && <ReadinessAdminPanel data={readiness} />}

        <Tabs defaultValue="exhibitors">
          <TabsList className="bg-zinc-100 rounded-none h-12 flex flex-wrap w-full overflow-x-auto justify-start">
            {[
              ["exhibitors","Prime Suppliers"], ["attendees","Attendees"],
              ["stations","Supplier Stations"], ["matchmaking","Matchmaking"],
              ["schedule","Event Programming"], ["fireside","Fireside Panelists"],
              ["homepage","Homepage Content"], ["updates","Executive Updates"],
              ["announcements","Announcements"], ["event","Event Config"],
              ["meetings","Meeting Requests"], ["magic","Magic Links"],
            ].map(([v,l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none uppercase tracking-[0.18em] text-xs" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="exhibitors" className="pt-6">
            <div className="card-flat overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left">
                    <tr className="border-b border-zinc-200">
                      <Th>Company</Th><Th>Contact</Th><Th>Payment</Th><Th>Status</Th>
                      <Th>Production Stage</Th><Th>Proof</Th><Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {exhibitors.map((e) => (
                      <tr key={e.id} className="border-b border-zinc-100 last:border-0">
                        <Td>
                          <div className="font-medium">{e.company_name}</div>
                          <div className="text-xs text-zinc-500">{e.email}</div>
                        </Td>
                        <Td>{e.primary_contact}<div className="text-xs text-zinc-500">{e.phone}</div></Td>
                        <Td><span className={`pill ${e.payment_status === "paid" ? "bg-[#A39171]/15 border-[#A39171]" : ""}`}>{e.payment_status}</span></Td>
                        <Td><span className={`pill ${e.status === "approved" ? "bg-black text-white border-black" : ""}`}>{e.status}</span></Td>
                        <Td>
                          <ProductionStageSelect exhibitor={e}
                            onSaved={(updated) => setExhibitors((es) => es.map((x) => x.id === updated.id ? updated : x))} />
                        </Td>
                        <Td>
                          <ProofControl exhibitor={e}
                            onSaved={(updated) => setExhibitors((es) => es.map((x) => x.id === updated.id ? updated : x))} />
                        </Td>
                        <Td>
                          <div className="flex gap-2 flex-wrap">
                            {e.status !== "approved" && <button onClick={() => setStatus(e.id, "approved")} data-testid={ADMIN.approveBtn} className="px-3 py-1 text-xs uppercase tracking-[0.18em] bg-black text-white"><Check size={12} /></button>}
                            {e.status !== "rejected" && <button onClick={() => setStatus(e.id, "rejected")} data-testid={ADMIN.rejectBtn} className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300"><X size={12} /></button>}
                            <button onClick={() => togglePayment(e)} data-testid={ADMIN.paymentToggleBtn} className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300">{e.payment_status === "paid" ? "Mark Unpaid" : "Mark Paid"}</button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                    {exhibitors.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-zinc-500">No suppliers yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="announcements" className="pt-6">
            <AnnouncementsTab list={announcements} onAdd={(a) => setAnnouncements([a, ...announcements])} onUpdate={(a) => setAnnouncements(announcements.map((x) => x.id === a.id ? a : x))} onRemove={(id) => setAnnouncements(announcements.filter((x) => x.id !== id))} />
          </TabsContent>

          <TabsContent value="attendees" className="pt-6">
            <AttendeesTab />
          </TabsContent>

          <TabsContent value="schedule" className="pt-6">
            <ScheduleTab list={sessions} onAdd={(s) => setSessions([...sessions, s])} onRemove={(id) => setSessions(sessions.filter((x) => x.id !== id))} onUpdate={(s) => setSessions(sessions.map((x) => x.id === s.id ? s : x))} />
          </TabsContent>

          <TabsContent value="stations" className="pt-6">
            <StationsTab exhibitors={exhibitors} />
          </TabsContent>

          <TabsContent value="matchmaking" className="pt-6">
            <MatchmakingTab exhibitors={exhibitors} />
          </TabsContent>

          <TabsContent value="fireside" className="pt-6">
            <FiresidePanelistsTab />
          </TabsContent>

          <TabsContent value="homepage" className="pt-6">
            <HomepageContentTab exhibitors={exhibitors} />
          </TabsContent>

          <TabsContent value="updates" className="pt-6">
            <ExecutiveUpdatesTab />
          </TabsContent>

          <TabsContent value="event" className="pt-6">
            <EventConfigTab />
          </TabsContent>

          <TabsContent value="meetings" className="pt-6">
            <div className="card-flat p-6">
              {meetings.length === 0 && <p className="text-sm text-zinc-500">No meeting requests yet.</p>}
              <div className="grid gap-3">
                {meetings.map((m) => (
                  <div key={m.id} className="border-b border-zinc-100 last:border-0 py-3">
                    <div className="text-sm font-medium">{m.company_name} <span className="text-zinc-500">· {m.contact_name}</span></div>
                    <div className="text-xs text-zinc-500">{m.email}</div>
                    <div className="text-xs text-[#A39171] mt-1">{(m.interest_categories || []).join(" · ")}</div>
                    {m.notes && <p className="text-sm text-zinc-600 mt-1">{m.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="magic" className="pt-6">
            <div className="card-flat p-6" data-testid={ADMIN.magicLinksTab}>
              <p className="text-sm text-zinc-500 mb-4">Email is currently MOCKED. Use these links to sign in until Resend is wired up.</p>
              <div className="grid gap-3">
                {magicLinks.map((m) => (
                  <div key={m.token} className="border-b border-zinc-100 last:border-0 py-3 flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <div className="text-sm font-medium">{m.email}</div>
                      <div className="text-xs text-zinc-500">Issued {new Date(m.created_at).toLocaleString()} {m.used ? "· used" : ""}</div>
                    </div>
                    <a href={`/verify?token=${m.token}`} className="btn-outline-black h-9 px-4 text-xs"><LinkIcon size={12} /> Open</a>
                  </div>
                ))}
                {magicLinks.length === 0 && <p className="text-sm text-zinc-500">No pending magic links.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white p-5">
      <div className="serif text-3xl">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
const Th = ({ children }) => <th className="text-xs uppercase tracking-[0.18em] text-zinc-500 px-4 py-3">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-4 align-top">{children}</td>;


/* ============================================================
   ATTENDEES TAB — unified view across Prime Suppliers, SBEs, T+L Attendees, Speakers, DA Staff.
   Backed by GET /admin/attendees and POST /admin/attendees/checkin.
   ============================================================ */
const ATTENDEE_TYPE_LABELS = {
  prime_supplier: "Prime Supplier",
  prime_additional_rep: "Prime · Additional Rep",
  sbe: "SBE",
  tl_attendee: "T+L Team",
  speaker: "Speaker",
  summit_guest: "Summit Guest",
  da_staff: "DA Staff",
};

export function AttendeesTab() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [headcount, setHeadcount] = useState(null);
  const [type, setType] = useState(""); // "" = all
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [staffModal, setStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", email: "", title: "", phone: "" });
  const [staffSaving, setStaffSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = type ? `?type=${type}` : "";
      const { data } = await api.get(`/admin/attendees${params}`);
      setRows(data.attendees || []);
      setCounts(data.counts || null);
      setHeadcount(data.headcount || null);
    } catch { toast.error("Could not load attendees."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [type]);

  const toggleCheckin = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post("/admin/attendees/checkin", {
        type: r.type, id: r.id, checked_in: !r.checked_in,
      });
      const a = data.attendee;
      setRows((cur) => cur.map((x) => x.id === r.id
        ? { ...x, checked_in: !!a.checked_in, checked_in_at: a.checked_in_at }
        : x));
      setCounts((c) => c ? { ...c, checked_in: c.checked_in + (!r.checked_in ? 1 : -1) } : c);
      toast.success(r.checked_in ? "Check-in reversed." : "Checked in.");
    } catch (e) { toast.error("Check-in update failed."); }
    finally { setBusyId(null); }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(t)
        || (r.email || "").toLowerCase().includes(t)
        || (r.company || "").toLowerCase().includes(t);
  });

  const addStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.name.trim() || !staffForm.email.trim()) return toast.error("Name and email are required.");
    setStaffSaving(true);
    try {
      await api.post("/admin/attendees/da-staff", {
        name: staffForm.name.trim(), email: staffForm.email.trim(),
        title: staffForm.title || null, phone: staffForm.phone || null,
      });
      toast.success("DA Staff member added.");
      setStaffModal(false);
      setStaffForm({ name: "", email: "", title: "", phone: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add staff member.");
    } finally { setStaffSaving(false); }
  };

  const REASSIGN_TARGETS = ["sbe", "tl_attendee", "summit_guest", "speaker", "da_staff"];

  const reassign = async (r, newType) => {
    if (!newType || newType === r.type) return;
    if (!window.confirm(`Reassign ${r.name || r.email} from ${ATTENDEE_TYPE_LABELS[r.type] || r.type} to ${ATTENDEE_TYPE_LABELS[newType] || newType}?`)) return;
    setBusyId(r.id);
    try {
      await api.patch(`/admin/attendees/${r.type}/${r.id}/reassign`, { new_type: newType });
      toast.success(`Reassigned to ${ATTENDEE_TYPE_LABELS[newType] || newType}.`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reassign failed."); }
    finally { setBusyId(null); }
  };

  const removeAttendee = async (r) => {
    if (!window.confirm(`Delete this ${ATTENDEE_TYPE_LABELS[r.type] || r.type} registration for ${r.name || r.email}? This cannot be undone.`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/attendees/${r.type}/${r.id}`);
      toast.success("Registration deleted.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
    finally { setBusyId(null); }
  };

  const [primeModal, setPrimeModal] = useState(null);
  const [primes, setPrimes] = useState([]);
  const [primeChoice, setPrimeChoice] = useState("");
  const [primeTitle, setPrimeTitle] = useState("");
  const [attachBusy, setAttachBusy] = useState(false);

  const openPrimeModal = async (r) => {
    setPrimeModal(r); setPrimeChoice(""); setPrimeTitle(r.title || "");
    if (primes.length === 0) {
      try {
        const { data } = await api.get("/admin/exhibitors");
        setPrimes((data.exhibitors || []).filter((e) => !e.is_da_support)
          .sort((a, b) => (a.company_name || "").localeCompare(b.company_name || "")));
      } catch { /* ignore */ }
    }
  };

  const attachToPrime = async () => {
    if (!primeChoice) return toast.error("Choose a Prime Supplier.");
    setAttachBusy(true);
    try {
      const { data } = await api.post(
        `/admin/attendees/${primeModal.type}/${primeModal.id}/attach-to-prime`,
        { exhibitor_id: primeChoice, title: primeTitle || null });
      toast.success(data.message || "Attached to Prime.");
      setPrimeModal(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Attach failed."); }
    finally { setAttachBusy(false); }
  };

  return (
    <div className="grid gap-6" data-testid="attendees-tab">
      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-px bg-[hsl(var(--line))]">
          <Stat label="Registration Records" value={counts.total} />
          <Stat label="Checked In" value={counts.checked_in} />
          <Stat label="Prime Suppliers" value={counts.prime_supplier} />
          <Stat label="SBE" value={counts.sbe} />
          <Stat label="T+L Team" value={counts.tl_attendee} />
          <Stat label="Summit Guests" value={counts.summit_guest} />
          <Stat label="Speakers" value={counts.speaker} />
        </div>
      )}

      {headcount && (
        <div className="card-flat p-5" data-testid="attendees-headcount">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="overline text-zinc-500">Registered Headcount — Individuals</div>
            <div className="text-sm font-semibold" data-testid="headcount-total">
              Total Registered Attendees: {headcount.total_people}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
            <span className="pill">Prime · Primary Reps: {headcount.prime_primary_reps}</span>
            <span className="pill">Prime · Additional Reps: {headcount.prime_additional_reps}</span>
            <span className="pill">SBE: {headcount.sbe}</span>
            <span className="pill">T+L Team: {headcount.tl_attendee}</span>
            <span className="pill">Speakers: {headcount.speaker}</span>
            {Object.entries(headcount.guest_types || {}).map(([k, v]) => (
              <span key={k} className="pill">{k}: {v}</span>
            ))}
            {headcount.da_staff > 0 && <span className="pill">DA Staff: {headcount.da_staff}</span>}
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">
            Counts individual people (not companies or stations) — for catering, name badges, seating, and check-in planning.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {["", "prime_supplier", "sbe", "tl_attendee", "summit_guest", "speaker", "da_staff"].map((t) => (
          <button key={t || "all"}
            onClick={() => setType(t)}
            data-testid={`attendee-filter-${t || "all"}`}
            className={`px-3 h-9 text-[11px] uppercase tracking-[0.18em] border ${type === t ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}>
            {t ? ATTENDEE_TYPE_LABELS[t] : "All"}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => setStaffModal(true)}
            data-testid="attendees-add-da-staff"
            className="h-9 px-3 text-[11px] uppercase tracking-[0.18em] border border-zinc-300 hover:border-black inline-flex items-center gap-2"
            title="Add a Display America staff member to the attendee list">
            <Plus size={12} /> Add DA Staff
          </button>
          <button
            onClick={async () => {
              try {
                const res = await api.get("/admin/attendees/export.xlsx", { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const today = new Date().toISOString().slice(0, 10);
                a.download = `Summit_Attendees_${today}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                toast.success("Attendee list downloaded.");
              } catch { toast.error("Download failed."); }
            }}
            data-testid="attendees-download-xlsx"
            className="h-9 px-3 text-[11px] uppercase tracking-[0.18em] border border-black bg-black text-white hover:bg-zinc-800 inline-flex items-center gap-2"
            title="Download all registered attendees as Excel (.xlsx) — for badge printing">
            <Download size={12} /> Download Excel
          </button>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…"
            data-testid="attendees-search" className="h-9 rounded-none w-full sm:w-72" />
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr className="border-b border-zinc-200">
                <Th>Type</Th><Th>Name</Th><Th>Company</Th><Th>Contact</Th>
                <Th>Check-In ID</Th><Th>Status</Th><Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-10 text-center text-zinc-500">Loading attendees…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-500">
                  No attendees{type ? ` for ${ATTENDEE_TYPE_LABELS[type]}` : ""} yet.
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={`${r.type}-${r.id}`} className="border-b border-zinc-100 last:border-0"
                    data-testid={`attendee-row-${r.id}`}>
                  <Td>
                    <span className="pill">{ATTENDEE_TYPE_LABELS[r.type] || r.type}</span>
                    {r.guest_type && <div className="text-[10px] text-zinc-500 mt-1">{r.guest_type}</div>}
                  </Td>
                  <Td>
                    <div className="font-medium">{r.name || "—"}</div>
                    {r.title && <div className="text-xs text-zinc-500">{r.title}</div>}
                  </Td>
                  <Td>{r.company || <span className="text-zinc-400">—</span>}</Td>
                  <Td>
                    <div>{r.email}</div>
                    {r.phone && <div className="text-xs text-zinc-500">{r.phone}</div>}
                  </Td>
                  <Td>
                    <span className="text-[11px] tabular-nums text-zinc-500">
                      {r.check_in_id ? r.check_in_id.slice(0, 8).toUpperCase() : "—"}
                    </span>
                  </Td>
                  <Td>
                    {r.checked_in ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] uppercase tracking-[0.18em]">
                        <CheckCircle2 size={12} /> Checked in
                      </span>
                    ) : (
                      <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Not checked in</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => toggleCheckin(r)}
                        disabled={busyId === r.id}
                        data-testid={`attendee-checkin-${r.id}`}
                        className={`px-3 py-1 text-xs uppercase tracking-[0.18em] border ${r.checked_in ? "border-zinc-300" : "bg-black text-white border-black"}`}>
                        {busyId === r.id ? "…" : r.checked_in ? "Undo" : "Check In"}
                      </button>
                      {r.type !== "prime_supplier" && r.type !== "prime_additional_rep" && (
                        <>
                          <select
                            value=""
                            disabled={busyId === r.id}
                            onChange={(e) => { reassign(r, e.target.value); e.target.value = ""; }}
                            data-testid={`attendee-reassign-${r.id}`}
                            title="Reassign this contact to a different attendee type"
                            className="text-xs border border-zinc-300 hover:border-black px-2 py-1 bg-white outline-none">
                            <option value="">Reassign…</option>
                            {REASSIGN_TARGETS.filter((t) => t !== r.type).map((t) => (
                              <option key={t} value={t}>→ {ATTENDEE_TYPE_LABELS[t] || t}</option>
                            ))}
                          </select>
                          <button onClick={() => openPrimeModal(r)}
                            disabled={busyId === r.id}
                            className="text-xs border border-zinc-300 hover:border-black px-2 py-1"
                            title="Attach this contact to their Prime Supplier as Additional Representative"
                            data-testid={`attendee-attach-prime-${r.id}`}>
                            → Prime
                          </button>
                          <button onClick={() => removeAttendee(r)}
                            disabled={busyId === r.id}
                            className="text-zinc-400 hover:text-red-600"
                            aria-label="Delete registration"
                            title="Delete this registration (e.g. a duplicate)"
                            data-testid={`attendee-delete-${r.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {primeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPrimeModal(null)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="attach-prime-modal">
            <div className="border-b border-zinc-200 p-5 flex items-start justify-between gap-4">
              <div>
                <div className="overline">Assign to Prime Supplier</div>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Attach <strong>{primeModal.name || primeModal.email}</strong> to a Prime Supplier as its
                  Additional Representative and remove this {ATTENDEE_TYPE_LABELS[primeModal.type] || primeModal.type} record.
                  If they're already that Prime's primary/additional rep, the duplicate is simply removed.
                </p>
              </div>
              <button onClick={() => setPrimeModal(null)} className="text-zinc-400 hover:text-black" aria-label="Close" data-testid="attach-prime-close"><X size={18} /></button>
            </div>
            <div className="p-5 grid gap-4">
              <div>
                <Label className="overline">Prime Supplier *</Label>
                <select value={primeChoice} onChange={(e) => setPrimeChoice(e.target.value)}
                  data-testid="attach-prime-select"
                  className="h-11 w-full border border-zinc-300 hover:border-black px-3 bg-white outline-none mt-2 text-sm">
                  <option value="">— Select a Prime Supplier —</option>
                  {primes.map((p) => {
                    const filled = (p.additional_representative || {}).email || (p.additional_representative || {}).first_name;
                    return <option key={p.id} value={p.id}>{p.company_name}{filled ? " (addl rep on file)" : ""}</option>;
                  })}
                </select>
              </div>
              <div>
                <Label className="overline">Title (optional)</Label>
                <Input value={primeTitle} onChange={(e) => setPrimeTitle(e.target.value)}
                  data-testid="attach-prime-title" className="h-11 rounded-none mt-2" placeholder="e.g. Sales Manager" />
              </div>
              <button onClick={attachToPrime} disabled={attachBusy} className="btn-black h-11" data-testid="attach-prime-submit">
                {attachBusy ? "Assigning…" : "Assign to Prime"}
              </button>
            </div>
          </div>
        </div>
      )}
      {staffModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setStaffModal(false)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="da-staff-modal">
            <div className="border-b border-zinc-200 p-5 flex items-start justify-between gap-4">
              <div>
                <div className="overline">Add DA Staff</div>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Adds a Display America team member to the attendee list — counted in headcount, badges, and event-day check-in. No email is sent.
                </p>
              </div>
              <button onClick={() => setStaffModal(false)} className="text-zinc-400 hover:text-black" aria-label="Close" data-testid="da-staff-close"><X size={18} /></button>
            </div>
            <form onSubmit={addStaff} className="p-5 grid gap-4">
              <div>
                <Label className="overline">Full Name *</Label>
                <Input required value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                  data-testid="da-staff-name" className="h-11 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Email *</Label>
                <Input required type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  data-testid="da-staff-email" className="h-11 rounded-none mt-2" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Title</Label>
                  <Input value={staffForm.title} onChange={(e) => setStaffForm({ ...staffForm, title: e.target.value })}
                    data-testid="da-staff-title" className="h-11 rounded-none mt-2" />
                </div>
                <div>
                  <Label className="overline">Phone</Label>
                  <Input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                    data-testid="da-staff-phone" className="h-11 rounded-none mt-2" />
                </div>
              </div>
              <button type="submit" disabled={staffSaving} className="btn-black h-11" data-testid="da-staff-submit">
                {staffSaving ? "Adding…" : "Add Staff Member"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AnnouncementsTab({ list, onAdd, onRemove, onUpdate }) {
  const AUD_LABEL_LOCAL = { all: "All", primes: "Primes", sbes: "SBEs", tl: "T+L Team", guests: "Guests" };
  const AUD_LABEL = AUD_LABEL_LOCAL;
  const STATUS_STYLE = {
    live:      { label: "Live",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    scheduled: { label: "Scheduled", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    archived:  { label: "Archived",  cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  };
  const EMPTY_FORM = { title: "", body: "", priority: "normal", audience: "all",
                       scheduled_for_local: "", expires_at_local: "", pinned: false };
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTplModal, setShowTplModal] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", title: "", body: "", priority: "normal", audience: "all" });
  const [analyticsFor, setAnalyticsFor] = useState(null); // { id, ... }
  const [analytics, setAnalytics] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [editFor, setEditFor] = useState(null);        // full announcement being edited
  const [editForm, setEditForm] = useState(null);      // draft for the edit modal
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => { loadTemplates(); }, []);
  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/admin/announcement-templates");
      setTemplates(data.templates || []);
    } catch { /* non-fatal */ }
  };

  // Convert an <input type="datetime-local"> value (America/New_York wall time)
  // to an ISO UTC string the backend can compare against.
  const etLocalToUtcIso = (local) => {
    if (!local) return null;
    // The datetime-local value is a naïve wall time entered in ET.
    // Build it as an ET-tagged ISO string, then convert to UTC.
    // Aug/Sept 2026 → EDT (UTC-4). We assume ET for this event calendar.
    // For a general-purpose solution we'd use Intl; here EDT is safe.
    return new Date(`${local}:00-04:00`).toISOString();
  };
  const utcIsoToEtLocal = (iso) => {
    if (!iso) return "";
    // Convert UTC → EDT wall time (yyyy-MM-ddTHH:mm)
    const d = new Date(iso);
    const et = new Date(d.getTime() - 4 * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${et.getUTCFullYear()}-${pad(et.getUTCMonth() + 1)}-${pad(et.getUTCDate())}T${pad(et.getUTCHours())}:${pad(et.getUTCMinutes())}`;
  };
  const fmtEt = (iso) => iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "—";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: form.title, body: form.body,
        priority: form.priority, audience: form.audience,
        scheduled_for: etLocalToUtcIso(form.scheduled_for_local),
        expires_at: etLocalToUtcIso(form.expires_at_local),
        pinned: !!form.pinned,
      };
      const { data } = await api.post("/announcements", payload);
      onAdd(data.announcement);
      setForm(EMPTY_FORM);
      toast.success(payload.scheduled_for ? "Announcement scheduled." : "Announcement posted.");
    } catch { toast.error("Failed to save announcement."); } finally { setSubmitting(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`); onRemove(id);
  };
  const changeAudience = async (id, aud) => {
    try {
      const { data } = await api.patch(`/announcements/${id}`, { audience: aud });
      if (onUpdate) onUpdate(data.announcement);
      toast.success(`Audience updated to ${AUD_LABEL[aud]}.`);
    } catch { toast.error("Could not update audience."); }
  };
  const togglePin = async (a) => {
    try {
      const { data } = await api.patch(`/announcements/${a.id}`, { pinned: !a.pinned });
      if (onUpdate) onUpdate(data.announcement);
      toast.success(data.announcement.pinned ? "Pinned to top." : "Unpinned.");
    } catch { toast.error("Could not update pin."); }
  };
  const publishNow = async (id) => {
    if (!window.confirm("Publish this scheduled announcement immediately?")) return;
    try {
      const { data } = await api.post(`/announcements/${id}/publish-now`);
      if (onUpdate) onUpdate(data.announcement);
      toast.success("Published.");
    } catch { toast.error("Could not publish."); }
  };
  const applyTemplate = (t) => {
    setForm((f) => ({ ...f, title: t.title, body: t.body, priority: t.priority, audience: t.audience }));
    toast.success(`Template "${t.name}" applied.`);
  };
  const saveTemplate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/announcement-templates", tplForm);
      setTemplates((ts) => [...ts, data.template]);
      setTplForm({ name: "", title: "", body: "", priority: "normal", audience: "all" });
      setShowTplModal(false);
      toast.success("Template saved.");
    } catch { toast.error("Could not save template."); }
  };
  const deleteTemplate = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/admin/announcement-templates/${tid}`);
      setTemplates((ts) => ts.filter((t) => t.id !== tid));
    } catch { toast.error("Could not delete template."); }
  };
  const openEdit = (a) => {
    setEditFor(a);
    setEditForm({
      title: a.title || "",
      body:  a.body  || "",
      priority: a.priority || "normal",
      audience: a.audience || "all",
      scheduled_for_local: utcIsoToEtLocal(a.scheduled_for),
      expires_at_local:    utcIsoToEtLocal(a.expires_at),
      pinned: !!a.pinned,
    });
  };
  const saveEdit = async () => {
    if (!editFor || !editForm) return;
    setSavingEdit(true);
    try {
      const payload = {
        title: editForm.title,
        body:  editForm.body,
        priority: editForm.priority,
        audience: editForm.audience,
        scheduled_for: etLocalToUtcIso(editForm.scheduled_for_local),
        expires_at:    etLocalToUtcIso(editForm.expires_at_local),
        pinned: !!editForm.pinned,
      };
      const { data } = await api.patch(`/announcements/${editFor.id}`, payload);
      if (onUpdate) onUpdate(data.announcement);
      toast.success("Announcement updated.");
      setEditFor(null); setEditForm(null);
    } catch { toast.error("Could not save changes."); }
    finally { setSavingEdit(false); }
  };
  const openAnalytics = async (a) => {
    setAnalyticsFor(a); setAnalytics(null);
    try {
      const { data } = await api.get(`/admin/announcements/${a.id}/analytics`);
      setAnalytics(data);
    } catch { toast.error("Could not load analytics."); setAnalyticsFor(null); }
  };
  const seedCalendar = async () => {
    if (!window.confirm("Seed the 15 pre-planned Summit announcements? Existing seeded entries will be updated in place (idempotent).")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/announcements/seed-summit-calendar");
      toast.success(`Seeded: ${data.created} created, ${data.updated} updated (of ${data.total}).`);
      const { data: fresh } = await api.get("/announcements");
      // Bubble up: brute-force replace whole list via onAdd sequentially would be noisy;
      // easiest is to reload the page section by refetching in parent — we do a soft reload.
      window.location.reload();
    } catch { toast.error("Seed failed."); } finally { setSeeding(false); }
  };

  return (
    <div className="grid gap-6">
      {/* ---- Composer (top row, full width) ---- */}
      <form onSubmit={submit} className="card-flat p-6 grid gap-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="overline">New announcement</div>
          <div className="flex items-center gap-2 flex-wrap">
            {templates.length > 0 && (
              <Select onValueChange={(v) => { const t = templates.find((x) => x.id === v); if (t) applyTemplate(t); }}>
                <SelectTrigger className="h-8 rounded-none text-[11px] uppercase tracking-[0.14em] min-w-[180px]" data-testid="ann-template-picker">
                  <SelectValue placeholder="Load template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <button type="button" onClick={() => setShowTplModal(true)}
                    className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-2 py-1"
                    data-testid="ann-template-new">
              + Template
            </button>
            <button type="button" onClick={seedCalendar} disabled={seeding}
                    className="text-[11px] uppercase tracking-[0.16em] border border-[#A39171] text-[#A39171] hover:bg-[#A39171]/10 px-2 py-1 disabled:opacity-50"
                    data-testid="ann-seed-summit-calendar">
              {seeding ? "Seeding…" : "Seed Summit Calendar"}
            </button>
          </div>
        </div>
        <div><Label className="overline">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-12 rounded-none mt-2" required data-testid="ann-title" /></div>
        <div><Label className="overline">Body</Label><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-none mt-2" required data-testid="ann-body" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="overline">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="h-12 rounded-none mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High Priority</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger className="h-12 rounded-none mt-2" data-testid="ann-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All participants</SelectItem>
                <SelectItem value="primes">Prime Suppliers only</SelectItem>
                <SelectItem value="sbes">SBEs only</SelectItem>
                <SelectItem value="tl">Travel + Leisure Team only</SelectItem>
                <SelectItem value="guests">Summit Guests only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="overline">Schedule for (ET)</Label>
            <Input type="datetime-local" value={form.scheduled_for_local}
                   onChange={(e) => setForm({ ...form, scheduled_for_local: e.target.value })}
                   className="h-12 rounded-none mt-2" data-testid="ann-scheduled-for" />
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Leave blank to publish immediately</div>
          </div>
          <div>
            <Label className="overline">Expires (ET, optional)</Label>
            <Input type="datetime-local" value={form.expires_at_local}
                   onChange={(e) => setForm({ ...form, expires_at_local: e.target.value })}
                   className="h-12 rounded-none mt-2" data-testid="ann-expires-at" />
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Auto-archives after this time</div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="ann-pinned" />
          <span>Pin to top of feed</span>
        </label>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          Prime-only announcements are hidden from SBE portals · SBE-only announcements are hidden from Prime portals · T+L reps always get an email copy on publish
        </div>
        <button disabled={submitting} className="btn-black" data-testid={ADMIN.announcementSubmit}>
          <Megaphone size={14} /> {form.scheduled_for_local ? "Schedule Announcement" : "Post Announcement"}
        </button>
      </form>

      {/* ---- Feed (full width, 2-column grid on desktop) ---- */}
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((a) => {
          const st = STATUS_STYLE[a.status] || STATUS_STYLE.live;
          return (
          <div key={a.id} className="card-flat p-5" data-testid={`ann-row-${a.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${st.cls}`} data-testid={`ann-status-${a.id}`}>{st.label}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 border-[#A39171] text-[#A39171] bg-[#A39171]/10">{AUD_LABEL[a.audience] || "All"}</span>
                  {a.priority === "high" && <span className="text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 border-red-300 text-red-700 bg-red-50">High</span>}
                  {a.pinned && <span className="text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 border-black text-black bg-white">Pinned</span>}
                </div>
                <div className="text-base font-medium mt-2">{a.title}</div>
                <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{a.body}</p>
                <div className="mt-3 text-[11px] text-zinc-500 space-y-0.5">
                  {a.created_at && <div data-testid={`ann-created-${a.id}`}>Created: {fmtEt(a.created_at)}</div>}
                  {a.published_at && <div data-testid={`ann-last-sent-${a.id}`}>Last sent: {fmtEt(a.published_at)}</div>}
                  {a.scheduled_for && !a.published_at && <div>Scheduled: {fmtEt(a.scheduled_for)}</div>}
                  {a.expires_at && <div>Expires: {fmtEt(a.expires_at)}</div>}
                </div>
              </div>
              <button onClick={() => remove(a.id)} className="text-zinc-400 hover:text-red-600 shrink-0" aria-label="Delete" data-testid={`ann-delete-${a.id}`}><Trash2 size={16} /></button>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2 flex-wrap">
              <button onClick={() => openEdit(a)}
                      className="text-[10px] uppercase tracking-[0.16em] px-2 py-1 border border-zinc-300 hover:border-black"
                      data-testid={`ann-edit-${a.id}`}>Edit</button>
              {a.status === "scheduled" && (
                <button onClick={() => publishNow(a.id)}
                        className="text-[10px] uppercase tracking-[0.16em] px-2 py-1 border border-black bg-black text-white hover:bg-zinc-800"
                        data-testid={`ann-publish-now-${a.id}`}>Publish Now</button>
              )}
              <button onClick={() => togglePin(a)}
                      className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 border ${a.pinned ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                      data-testid={`ann-pin-${a.id}`}>{a.pinned ? "Unpin" : "Pin"}</button>
              <button onClick={() => openAnalytics(a)}
                      className="text-[10px] uppercase tracking-[0.16em] px-2 py-1 border border-zinc-300 hover:border-black"
                      data-testid={`ann-analytics-${a.id}`}>Analytics</button>
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 ml-1">Audience:</span>
              {["all", "primes", "sbes", "tl", "guests"].map((aud) => (
                <button key={aud} type="button"
                        onClick={() => changeAudience(a.id, aud)}
                        disabled={(a.audience || "all") === aud}
                        className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 border ${(a.audience || "all") === aud ? "border-black bg-black text-white cursor-default" : "border-zinc-300 hover:border-black"}`}
                        data-testid={`ann-set-audience-${a.id}-${aud}`}>
                  {AUD_LABEL[aud]}
                </button>
              ))}
            </div>
          </div>
        );})}
        {list.length === 0 && (
          <div className="card-flat p-6 text-center text-sm text-zinc-500">
            No announcements yet. Use the composer or click <b>Seed Summit Calendar</b>.
          </div>
        )}
      </div>

      {/* ---- Template modal ---- */}
      {showTplModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTplModal(false)}>
          <div className="bg-white w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Announcement templates</div>
              <button onClick={() => setShowTplModal(false)} className="text-zinc-400 hover:text-black"><X size={18} /></button>
            </div>
            {templates.length > 0 && (
              <div className="mb-4 border border-zinc-200">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 last:border-b-0">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{t.title}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => { applyTemplate(t); setShowTplModal(false); }} className="text-[10px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-2 py-1">Use</button>
                      <button onClick={() => deleteTemplate(t.id)} className="text-zinc-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={saveTemplate} className="grid gap-3">
              <div className="overline">New template</div>
              <Input placeholder="Template name" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} className="h-11 rounded-none" required />
              <Input placeholder="Title" value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} className="h-11 rounded-none" required />
              <Textarea rows={4} placeholder="Body" value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} className="rounded-none" required />
              <div className="grid grid-cols-2 gap-3">
                <Select value={tplForm.priority} onValueChange={(v) => setTplForm({ ...tplForm, priority: v })}>
                  <SelectTrigger className="h-11 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
                <Select value={tplForm.audience} onValueChange={(v) => setTplForm({ ...tplForm, audience: v })}>
                  <SelectTrigger className="h-11 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="primes">Primes</SelectItem><SelectItem value="sbes">SBEs</SelectItem><SelectItem value="tl">T+L Team</SelectItem><SelectItem value="guests">Guests</SelectItem></SelectContent>
                </Select>
              </div>
              <button className="btn-black" data-testid="ann-template-save"><Plus size={14} /> Save Template</button>
            </form>
          </div>
        </div>
      )}


      {/* ---- Edit announcement modal ---- */}
      {editFor && editForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setEditFor(null); setEditForm(null); }}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ann-edit-modal">
            <div className="border-b border-zinc-200 p-5 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
              <div>
                <div className="overline">Edit Announcement</div>
                <div className="text-lg font-medium mt-1">{editFor.title}</div>
                <p className="text-[11px] text-zinc-500 mt-1">
                  {editFor.status === "live" && editFor.published_at
                    ? "This announcement was already sent — edits update the text shown to participants, but no new email is triggered."
                    : "Changes save immediately. If it's scheduled, the new schedule replaces the old one."}
                </p>
              </div>
              <button onClick={() => { setEditFor(null); setEditForm(null); }} className="text-zinc-400 hover:text-black" aria-label="Close" data-testid="ann-edit-close"><X size={18} /></button>
            </div>
            <div className="p-5 grid gap-4">
              <div>
                <Label className="overline">Title</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-2 rounded-none" data-testid="ann-edit-title" />
              </div>
              <div>
                <Label className="overline">Message</Label>
                <Textarea rows={5} value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} className="mt-2 rounded-none" data-testid="ann-edit-body" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Priority</Label>
                  <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="ann-edit-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline">Audience</Label>
                  <Select value={editForm.audience} onValueChange={(v) => setEditForm({ ...editForm, audience: v })}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="ann-edit-audience"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Participants</SelectItem>
                      <SelectItem value="primes">Primes Only</SelectItem>
                      <SelectItem value="sbes">SBEs Only</SelectItem>
                      <SelectItem value="tl">T+L Team Only</SelectItem>
                      <SelectItem value="guests">Summit Guests Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline">Scheduled For (ET)</Label>
                  <Input type="datetime-local" value={editForm.scheduled_for_local}
                         onChange={(e) => setEditForm({ ...editForm, scheduled_for_local: e.target.value })}
                         className="mt-2 rounded-none" data-testid="ann-edit-scheduled" />
                  <p className="text-[10px] text-zinc-500 mt-1">Leave blank to publish immediately when saved.</p>
                </div>
                <div>
                  <Label className="overline">Expires (ET)</Label>
                  <Input type="datetime-local" value={editForm.expires_at_local}
                         onChange={(e) => setEditForm({ ...editForm, expires_at_local: e.target.value })}
                         className="mt-2 rounded-none" data-testid="ann-edit-expires" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!editForm.pinned}
                       onChange={(e) => setEditForm({ ...editForm, pinned: e.target.checked })}
                       data-testid="ann-edit-pinned" />
                <span>Pin to top of the feed</span>
              </label>
            </div>
            <div className="border-t border-zinc-200 p-4 flex items-center gap-3 justify-end sticky bottom-0 bg-white">
              <button onClick={() => { setEditFor(null); setEditForm(null); }}
                      className="text-[11px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black px-3 py-2"
                      data-testid="ann-edit-cancel">Cancel</button>
              <button onClick={saveEdit} disabled={savingEdit || !editForm.title || !editForm.body}
                      className="btn-black inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="ann-edit-save">
                <Check size={14} /> {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Analytics drawer ---- */}
      {analyticsFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end" onClick={() => setAnalyticsFor(null)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Read Receipts</div>
              <button onClick={() => setAnalyticsFor(null)} className="text-zinc-400 hover:text-black"><X size={18} /></button>
            </div>
            <div className="text-base font-medium">{analyticsFor.title}</div>
            {!analytics && <div className="text-sm text-zinc-500 mt-4">Loading…</div>}
            {analytics && (
              <>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="border border-zinc-200 p-3">
                    <div className="text-2xl font-medium">{analytics.total_recipients}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Audience</div>
                  </div>
                  <div className="border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-2xl font-medium text-emerald-700">{analytics.viewed_count}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 mt-1">Viewed</div>
                  </div>
                  <div className="border border-amber-200 bg-amber-50 p-3">
                    <div className="text-2xl font-medium text-amber-700">{analytics.unread_count}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 mt-1">Unread</div>
                  </div>
                </div>
                {analytics.viewed.length > 0 && (
                  <div className="mt-6">
                    <div className="overline mb-2">Viewed by</div>
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {analytics.viewed.map((v) => (
                        <div key={v.user_id} className="px-3 py-2 text-sm">
                          <div className="font-medium">{v.name || v.email}</div>
                          <div className="text-[11px] text-zinc-500">{v.company || v.kind} · {fmtEt(v.last_viewed_at)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {analytics.unread.length > 0 && (
                  <div className="mt-6">
                    <div className="overline mb-2">Not yet viewed</div>
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {analytics.unread.map((v) => (
                        <div key={v.user_id} className="px-3 py-2 text-sm">
                          <div className="font-medium">{v.name || v.email}</div>
                          <div className="text-[11px] text-zinc-500">{v.company || v.kind}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ScheduleTab({ list, onAdd, onRemove, onUpdate }) {
  const [form, setForm] = useState({ title: "", kind: "session", topic: "", speaker: "", description: "", location: "", start: "", end: "", order: 0 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/sessions", form);
      onAdd(data.session);
      setForm({ title: "", kind: "session", topic: "", speaker: "", description: "", location: "", start: "", end: "", order: 0 });
      toast.success("Session added.");
    } catch (er) { toast.error("Failed"); }
  };
  const remove = async (id) => { await api.delete(`/sessions/${id}`); onRemove(id); };
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
      <form onSubmit={submit} className="card-flat p-6 grid gap-4">
        <div className="overline">Add a session to the Event Programming Center</div>
        <Input placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-none" data-testid="add-session-title" />
        <div className="grid grid-cols-2 gap-3">
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger className="h-11 rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["session","fireside","showcase","breakout","networking","meal","opening","closing"].map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input placeholder="Topic (optional)" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="h-11 rounded-none" />
        </div>
        <Input placeholder="Speaker / Moderator" value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} className="h-11 rounded-none" />
        <Input placeholder="Location / Room" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-11 rounded-none" />
        <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="overline">Start</Label><Input type="datetime-local" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value + ":00" })} className="h-11 rounded-none mt-2" /></div>
          <div><Label className="overline">End</Label><Input type="datetime-local" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value + ":00" })} className="h-11 rounded-none mt-2" /></div>
        </div>
        <button className="btn-black" data-testid="add-session-btn"><Plus size={14} /> Add Session</button>
      </form>
      <div className="grid gap-3">
        {list.sort((a, b) => a.order - b.order).map((s) => (
          <SessionRow key={s.id} session={s} onRemove={remove} onUpdate={onUpdate} />
        ))}
        {list.length === 0 && <div className="card-flat p-6 text-sm text-zinc-500">No sessions.</div>}
      </div>
    </div>
  );
}

function SessionRow({ session, onRemove, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: session.title, kind: session.kind, topic: session.topic || "",
    speaker: session.speaker || "", description: session.description || "",
    location: session.location || "",
    start: session.start, end: session.end, order: session.order,
  });
  const save = async () => {
    try {
      const { data } = await api.put(`/sessions/${session.id}`, form);
      onUpdate(data.session);
      setEditing(false);
      toast.success("Session updated.");
    } catch (e) { toast.error("Update failed"); }
  };
  if (!editing) return (
    <div className="card-flat p-4 flex items-center justify-between gap-4" data-testid={`session-row-${session.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{session.title}</div>
        <div className="text-xs text-zinc-500 mt-1">
          {new Date(session.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          {" — "}
          {new Date(session.end).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          {" · "}
          {session.location || "—"}
          {" · "}
          <span className="text-[#A39171] uppercase tracking-[0.14em]">{session.kind}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-[0.14em] px-3 h-9 border border-zinc-300 hover:border-black" data-testid={`session-edit-${session.id}`}>Edit</button>
        <button onClick={() => onRemove(session.id)} className="text-zinc-400 hover:text-black"><Trash2 size={16} /></button>
      </div>
    </div>
  );
  return (
    <div className="card-flat p-4 grid gap-3">
      <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-10 rounded-none" data-testid={`session-title-${session.id}`} />
      <div className="grid grid-cols-2 gap-3">
        <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
          <SelectTrigger className="h-10 rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["session","fireside","showcase","breakout","networking","meal","opening","closing"].map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input placeholder="Topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="h-10 rounded-none" />
      </div>
      <Input placeholder="Speaker / Moderator" value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} className="h-10 rounded-none" />
      <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="h-10 rounded-none" />
      <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" />
      <div className="grid grid-cols-2 gap-3">
        <Input type="datetime-local" value={form.start.slice(0,16)} onChange={(e) => setForm({ ...form, start: e.target.value + ":00" })} className="h-10 rounded-none" />
        <Input type="datetime-local" value={form.end.slice(0,16)} onChange={(e) => setForm({ ...form, end: e.target.value + ":00" })} className="h-10 rounded-none" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-[0.14em] px-4 h-9 border border-zinc-300">Cancel</button>
        <button onClick={save} className="btn-black h-9 px-5 text-xs" data-testid={`session-save-${session.id}`}>Save</button>
      </div>
    </div>
  );
}

export function EventConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/event").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <div className="overline">Loading…</div>;
  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: cfg.name, date: cfg.date, date_iso: cfg.date_iso,
        event_start_iso: cfg.event_start_iso, event_end_iso: cfg.event_end_iso,
        location: cfg.location, address: cfg.address, tagline: cfg.tagline,
        registration_time: cfg.registration_time, opening_time: cfg.opening_time,
        lunch_time: cfg.lunch_time, showcase_time: cfg.showcase_time, closing_time: cfg.closing_time,
      };
      await api.put("/admin/event-config", payload);
      toast.success("Event configuration saved.");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const F = ({ label, k, type = "text" }) => (
    <div><Label className="overline">{label}</Label>
      <Input type={type} value={cfg[k] || ""} onChange={(e) => upd(k, e.target.value)} className="h-11 rounded-none mt-2" data-testid={`evt-${k}`} />
    </div>
  );
  return (
    <div className="card-flat p-6 md:p-8 grid gap-5 max-w-3xl">
      <h3 className="text-lg">Branding</h3>
      <F label="Event Name" k="name" />
      <F label="Tagline" k="tagline" />
      <div className="grid md:grid-cols-2 gap-5">
        <F label="Location (Name)" k="location" />
        <F label="Address" k="address" />
      </div>
      <h3 className="text-lg mt-4">Dates</h3>
      <div className="grid md:grid-cols-3 gap-5">
        <F label="Date (display)" k="date" />
        <F label="Start (ISO w/ tz)" k="event_start_iso" />
        <F label="End (ISO w/ tz)" k="event_end_iso" />
      </div>
      <h3 className="text-lg mt-4">Schedule Highlights (used on Home info card)</h3>
      <div className="grid md:grid-cols-2 gap-5">
        <F label="Registration Opens" k="registration_time" />
        <F label="Opening Session" k="opening_time" />
        <F label="Networking Lunch" k="lunch_time" />
        <F label="Supplier Showcase Opens" k="showcase_time" />
        <F label="Closing" k="closing_time" />
      </div>
      <div className="pt-4 border-t border-zinc-200 flex justify-end">
        <button disabled={saving} onClick={save} className="btn-black" data-testid="evt-save">
          {saving ? "Saving…" : "Save Event Config"}
        </button>
      </div>
    </div>
  );
}


function ReadinessAdminPanel({ data }) {
  const pct = data.overall_readiness_pct || 0;
  const barTone = pct >= 90 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500";
  const bandChip = (band) => band === "green"
    ? { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Ready" }
    : band === "yellow"
    ? { cls: "bg-amber-50 text-amber-900 border-amber-200", label: "On track" }
    : { cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Action" };

  const rows = [
    { key: "registered", label: "Registered", value: data.registered, denom: data.total },
    { key: "paid", label: "Paid", value: data.paid, denom: data.total },
    { key: "artwork", label: "Artwork approved", value: data.artwork_uploaded, denom: data.total },
    { key: "capability", label: "Capability statements", value: data.capability_uploaded, denom: data.total },
    { key: "matchmaking", label: "Matchmaking completed", value: data.matchmaking_completed, denom: data.total },
    { key: "outstanding", label: "Outstanding tasks", value: data.outstanding_tasks_total, denom: null },
  ];

  return (
    <div className="card-flat p-6 md:p-8 mb-10" data-testid="admin-readiness-panel">
      <div className="grid lg:grid-cols-[auto_1fr] gap-8 items-center">
        <div>
          <div className="overline mb-3"><span className="accent-rule" />Supplier Readiness</div>
          <div className="serif text-6xl md:text-7xl tabular-nums leading-none">
            <CountUp value={pct} suffix="%" />
          </div>
          <div className="text-sm text-zinc-500 mt-3">Across {data.total} supplier{data.total === 1 ? "" : "s"}</div>
        </div>
        <div className="min-w-0">
          <div className="h-1 bg-zinc-100">
            <div className={`h-full ${barTone}`} style={{ width: `${pct}%`, transition: "width 0.8s ease" }} />
          </div>
          <div className="mt-6 grid grid-cols-3 sm:grid-cols-6 gap-px bg-[hsl(var(--line))]">
            {rows.map((r) => (
              <div key={r.key} className="bg-white p-4">
                <div className="serif text-3xl tabular-nums leading-none">
                  <CountUp value={r.value || 0} />
                </div>
                <div className="overline mt-2 text-[10px]">{r.label}</div>
                {r.denom != null && (
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 mt-1">of {r.denom}</div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-px bg-[hsl(var(--line))]">
            <div className="bg-white p-4">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Ready</div></div>
              <div className="serif text-3xl mt-2 tabular-nums"><CountUp value={data.ready_green} /></div>
            </div>
            <div className="bg-white p-4">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-500" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">On track</div></div>
              <div className="serif text-3xl mt-2 tabular-nums"><CountUp value={data.on_track_yellow} /></div>
            </div>
            <div className="bg-white p-4">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-rose-500" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Action needed</div></div>
              <div className="serif text-3xl mt-2 tabular-nums"><CountUp value={data.action_red} /></div>
            </div>
          </div>
        </div>
      </div>

      {(data.suppliers || []).length > 0 && (
        <div className="mt-8 overflow-x-auto">
          <div className="overline mb-3">Per supplier · lowest score first</div>
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <tr className="border-b border-zinc-200">
                <Th>Supplier</Th><Th>Score</Th><Th>Status</Th><Th>Next action</Th><Th>Missing</Th>
              </tr>
            </thead>
            <tbody>
              {data.suppliers.slice(0, 25).map((s) => {
                const chip = bandChip(s.band);
                return (
                  <tr key={s.id} className="border-b border-zinc-100" data-testid={`admin-readiness-row-${s.id}`}>
                    <Td>
                      <div className="text-zinc-800">{s.company_name || "—"}</div>
                      <div className="overline mt-1 text-zinc-500">{s.industry || "—"}</div>
                    </Td>
                    <Td><span className="tabular-nums font-medium">{s.score}%</span></Td>
                    <Td><span className={`inline-flex items-center gap-1 h-6 px-2 border text-[10px] uppercase tracking-[0.18em] ${chip.cls}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${s.band === "green" ? "bg-emerald-500" : s.band === "yellow" ? "bg-amber-500" : "bg-rose-500"}`} />
                      {chip.label}
                    </span></Td>
                    <Td className="text-zinc-700">{s.next_action || "—"}</Td>
                    <Td><span className="tabular-nums">{s.missing_count}</span></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ============================================================
   Supplier Stations Management
   ============================================================ */
export function StationsTab({ exhibitors }) {
  const [stations, setStations] = useState([]);
  const [showcaseCats, setShowcaseCats] = useState([]);
  const [savingId, setSavingId] = useState(null);
  useEffect(() => {
    api.get("/admin/stations").then(({ data }) => setStations(data.stations || []));
    api.get("/event").then(({ data }) => setShowcaseCats(data.showcase_categories || []));
  }, []);
  const update = async (station, patch) => {
    setSavingId(station.id);
    try {
      const { data } = await api.put(`/admin/stations/${station.id}`, patch);
      setStations((all) => all.map((s) => s.id === station.id ? data.station : s));
      toast.success(`Station ${station.label} updated.`);
    } catch (e) { toast.error("Update failed"); }
    finally { setSavingId(null); }
  };
  const filled = stations.filter(s => s.exhibitor_id).length;
  const approved = stations.filter(s => s.status === "approved").length;
  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--line))]" data-testid="stations-header">
        <Stat label="Stations Total" value={stations.length || 15} />
        <Stat label="Assigned" value={filled} />
        <Stat label="Approved" value={approved} />
        <Stat label="Available" value={(stations.length || 15) - filled} />
      </div>
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-zinc-50 text-left">
              <tr className="border-b border-zinc-200">
                <Th>Station</Th><Th>Prime Supplier</Th><Th>Showcase Category</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {stations.map((s) => (
                <tr key={s.id} className="border-b border-zinc-100" data-testid={`station-row-${s.label}`}>
                  <Td>
                    <div className="font-medium">{s.label}</div>
                    <div className="text-xs text-zinc-500">#{s.number}</div>
                  </Td>
                  <Td>
                    <Select
                      value={s.exhibitor_id || "__none__"}
                      onValueChange={(v) => update(s, { exhibitor_id: v === "__none__" ? null : v })}>
                      <SelectTrigger className="h-9 rounded-none min-w-[220px]" data-testid={`station-assign-${s.label}`}>
                        <SelectValue placeholder="— unassigned —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— unassigned —</SelectItem>
                        {exhibitors.map((e) => (
                          <SelectItem key={e.id} value={e.id}>{e.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Td>
                  <Td>
                    <Select
                      value={s.showcase_category || "__none__"}
                      onValueChange={(v) => update(s, { showcase_category: v === "__none__" ? null : v })}>
                      <SelectTrigger className="h-9 rounded-none min-w-[180px]"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {showcaseCats.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Td>
                  <Td>
                    <span className={`pill ${s.status === "approved" ? "bg-black text-white border-black" : ""}`}>{s.status}</span>
                  </Td>
                  <Td>
                    <div className="flex gap-2 flex-wrap">
                      {s.status !== "approved" && s.exhibitor_id && (
                        <button onClick={() => update(s, { status: "approved" })}
                          disabled={savingId === s.id}
                          className="px-3 py-1 text-xs uppercase tracking-[0.18em] bg-black text-white"
                          data-testid={`station-approve-${s.label}`}>Approve</button>
                      )}
                      {s.status === "approved" && (
                        <button onClick={() => update(s, { status: "assigned" })}
                          className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300">Unapprove</button>
                      )}
                      {s.exhibitor_id && (
                        <button onClick={() => update(s, { exhibitor_id: null, status: "available", showcase_category: null })}
                          className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300 text-zinc-500">Clear</button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   Business Matchmaking Appointments
   ============================================================ */
export function MatchmakingTab({ exhibitors }) {
  const [apts, setApts] = useState([]);
  const [tlReps, setTlReps] = useState([]);
  const [sbes, setSbes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({
    tl_participant_id: "", counterpart_type: "prime",
    prime_supplier_id: "", sbe_id: "",
    location_type: "station", table_number: "", round_index: "",
    start_iso: "2026-09-11T13:00", end_iso: "2026-09-11T13:30", notes: "",
  });
  useEffect(() => {
    api.get("/admin/appointments").then(({ data }) => setApts(data.appointments || []));
    api.get("/admin/attendees", { params: { type: "tl_attendee" } }).then(({ data }) => setTlReps(data.attendees || [])).catch(() => {});
    api.get("/admin/attendees", { params: { type: "sbe" } }).then(({ data }) => setSbes(data.attendees || [])).catch(() => {});
    api.get("/admin/matchmaking/config").then(({ data }) => setSlots(data.rounds || [])).catch(() => {});
  }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.tl_participant_id) return toast.error("Select a T+L team member.");
    if (form.counterpart_type === "prime" && !form.prime_supplier_id) return toast.error("Select a Prime Supplier.");
    if (form.counterpart_type === "sbe" && !form.sbe_id) return toast.error("Select an SBE.");
    if (form.location_type === "table" && (form.table_number === "" || form.round_index === ""))
      return toast.error("Select a table and a time slot.");
    if (form.location_type === "station" && (!form.start_iso || !form.end_iso))
      return toast.error("Start and end times are required for station meetings.");
    try {
      const payload = {
        tl_participant_id: form.tl_participant_id,
        location_type: form.location_type,
        notes: form.notes || null,
      };
      if (form.counterpart_type === "prime") payload.prime_supplier_id = form.prime_supplier_id;
      else payload.sbe_id = form.sbe_id;
      if (form.location_type === "table") {
        payload.table_number = Number(form.table_number);
        payload.round_index = Number(form.round_index);
      } else {
        payload.start_iso = form.start_iso.length === 16 ? form.start_iso + ":00" : form.start_iso;
        payload.end_iso = form.end_iso.length === 16 ? form.end_iso + ":00" : form.end_iso;
      }
      const { data } = await api.post("/admin/appointments", payload);
      setApts((a) => [...a, data.appointment].sort((x, y) => (x.start_iso || "").localeCompare(y.start_iso || "")));
      setForm({ ...form, tl_participant_id: "", prime_supplier_id: "", sbe_id: "", table_number: "", round_index: "", notes: "" });
      toast.success("Sourcing meeting scheduled — both parties have been emailed.");
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Could not create the meeting.");
    }
  };
  const cancel = async (id) => {
    try { const { data } = await api.put(`/admin/appointments/${id}`, { status: "cancelled" });
      setApts((a) => a.map((x) => x.id === id ? data.appointment : x));
      toast.success("Meeting cancelled.");
    } catch (e) { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this meeting?")) return;
    try { await api.delete(`/admin/appointments/${id}`); setApts((a) => a.filter((x) => x.id !== id)); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="grid gap-6" data-testid="matchmaking-tab">
      <RunbookLinkCard />
      <PrimeBriefLinkCard />
      <DataHygieneCard />
      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
      <form onSubmit={add} className="card-flat p-6 grid gap-4 content-start self-start lg:sticky lg:top-4">
        <div>
          <div className="overline">Schedule a T+L sourcing meeting</div>
          <p className="text-[11px] text-zinc-500 mt-1">
            T+L sourcing team meets a Prime or SBE — at the Prime's showcase station (any afternoon time) or at a matchmaking table (M-table time slots). Conflicts with formal matchmaking are hard-blocked. Both parties are notified by email.
          </p>
        </div>
        <div>
          <Label className="overline">T+L Team Member *</Label>
          <Select value={form.tl_participant_id || undefined} onValueChange={(v) => setForm({ ...form, tl_participant_id: v })}>
            <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-tl-rep"><SelectValue placeholder="Select from registered T+L Team" /></SelectTrigger>
            <SelectContent>
              {tlReps.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}{t.title ? ` — ${t.title}` : ""}</SelectItem>))}
            </SelectContent>
          </Select>
          {tlReps.length === 0 && <p className="text-[11px] text-amber-700 mt-1">No T+L Team attendees registered yet — they must register first.</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="overline">Meet With *</Label>
            <Select value={form.counterpart_type} onValueChange={(v) => setForm({ ...form, counterpart_type: v, location_type: v === "sbe" ? "table" : form.location_type })}>
              <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-counterpart-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prime">Prime Supplier</SelectItem>
                <SelectItem value="sbe">SBE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">{form.counterpart_type === "prime" ? "Prime Supplier *" : "SBE *"}</Label>
            {form.counterpart_type === "prime" ? (
              <Select value={form.prime_supplier_id || undefined} onValueChange={(v) => setForm({ ...form, prime_supplier_id: v })}>
                <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-supplier"><SelectValue placeholder="Select a Prime" /></SelectTrigger>
                <SelectContent>
                  {exhibitors.map((e) => (<SelectItem key={e.id} value={e.id}>{e.company_name}</SelectItem>))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={form.sbe_id || undefined} onValueChange={(v) => setForm({ ...form, sbe_id: v })}>
                <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-sbe"><SelectValue placeholder="Select an SBE" /></SelectTrigger>
                <SelectContent>
                  {sbes.map((s) => (<SelectItem key={s.id} value={s.id}>{s.company || s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <div>
          <Label className="overline">Location *</Label>
          <Select value={form.location_type} onValueChange={(v) => setForm({ ...form, location_type: v })}>
            <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-location-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="station" disabled={form.counterpart_type === "sbe"}>Prime's Showcase Station (afternoon, free time)</SelectItem>
              <SelectItem value="table">Matchmaking Table M-1 – M-8 (time slots)</SelectItem>
            </SelectContent>
          </Select>
          {form.counterpart_type === "sbe" && <p className="text-[11px] text-zinc-500 mt-1">SBEs don't have a station — SBE meetings use a matchmaking table.</p>}
        </div>
        {form.location_type === "table" ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Table *</Label>
              <Select value={form.table_number || undefined} onValueChange={(v) => setForm({ ...form, table_number: v })}>
                <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-table"><SelectValue placeholder="M-#" /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8].map((n) => (<SelectItem key={n} value={String(n)}>M-{n}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Time Slot *</Label>
              <Select value={form.round_index !== "" ? String(form.round_index) : undefined} onValueChange={(v) => setForm({ ...form, round_index: v })}>
                <SelectTrigger className="h-11 rounded-none mt-2" data-testid="apt-slot"><SelectValue placeholder="Select a slot" /></SelectTrigger>
                <SelectContent>
                  {slots.map((r) => (<SelectItem key={r.index} value={String(r.index)}>{r.label}{r.slot_type === "open" ? " · Open Networking" : ""}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Start (12–4 PM ET) *</Label><Input type="datetime-local" value={form.start_iso} onChange={(e) => setForm({ ...form, start_iso: e.target.value })} className="h-11 rounded-none mt-2" data-testid="apt-start" /></div>
            <div><Label className="overline">End *</Label><Input type="datetime-local" value={form.end_iso} onChange={(e) => setForm({ ...form, end_iso: e.target.value })} className="h-11 rounded-none mt-2" data-testid="apt-end" /></div>
          </div>
        )}
        <Textarea rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" />
        <button className="btn-black" data-testid="apt-add"><Plus size={14} /> Schedule meeting</button>
      </form>
      <div className="grid gap-3">
        {apts.length === 0 && <div className="card-flat p-6 text-sm text-zinc-500">No T+L sourcing meetings yet.</div>}
        {apts.map((a) => (
          <div key={a.id} className={`card-flat p-4 ${a.status === "cancelled" ? "opacity-60" : ""}`} data-testid={`apt-row-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{a.tl_leader_name} <span className="text-zinc-400">×</span> {a.prime_supplier?.company_name || a.sbe?.company || a.sbe_name || "—"}</div>
                {a.sbe?.name && <div className="text-xs text-zinc-500 mt-1">SBE contact · {a.sbe.name}</div>}
                <div className="text-xs text-zinc-500 mt-1">
                  {a.start_iso && new Date(a.start_iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })}
                  {a.end_iso && <>{" → "}{new Date(a.end_iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET</>}
                  {" · "}{a.location}
                </div>
                {a.notes && <div className="text-xs text-zinc-600 mt-2">{a.notes}</div>}
                <span className={`pill mt-2 inline-flex ${a.status === "cancelled" ? "bg-zinc-100" : a.status === "completed" ? "bg-emerald-50 border-emerald-200" : "bg-[#A39171]/15 border-[#A39171]"}`}>{a.status}</span>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {a.status !== "cancelled" && (
                  <button onClick={() => cancel(a.id)} className="text-xs uppercase tracking-[0.14em] px-3 h-8 border border-zinc-300 hover:border-black">Cancel</button>
                )}
                <button onClick={() => remove(a.id)} className="text-zinc-400 hover:text-black text-xs">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}


/* Runbook link card — surfaces the operating procedure to admins at the
   top of the Matchmaking tab and Console. Purpose: no team member should
   ever have to guess how to build, validate, and publish a schedule. */
function RunbookLinkCard() {
  return (
    <div className="card-flat p-5 border-l-4 border-l-[#A39171] bg-[#FBF8F1]/70" data-testid="rb-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <BookOpen size={20} className="text-[#7A6A4A] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="overline">Ops Runbook · Business Matchmaking</div>
            <div className="serif text-xl mt-1">Step-by-step instructions for the matchmaking team</div>
            <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
              Walk your team through pre-flight, building meetings, the pre-publish guardrail, publishing, monitoring the notification log, and day-of ops. Read it top-to-bottom before you start scheduling — printable for your ops binder.
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to="/admin/matchmaking/runbook"
                className="btn-black inline-flex items-center gap-2"
                data-testid="rb-card-open">
            Open Runbook <ArrowRight size={14} />
          </Link>
          <Link to="/admin/matchmaking"
                className="btn-outline-black inline-flex items-center gap-2"
                data-testid="rb-card-console">
            Console
          </Link>
        </div>
      </div>
    </div>
  );
}



/* Prime Supplier Matchmaking Brief link card — Travel + Leisure
   sourcing leadership uses this URL to review every Prime's profile
   + matchmaking interests when picking SBEs to invite. */
function PrimeBriefLinkCard() {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/matchmaking/brief-token")
      .then(({ data }) => setToken(data.token))
      .catch(() => {});
  }, []);
  const publicUrl = token ? `${window.location.origin}/matchmaking/brief/${token}` : "";
  const rotate = async () => {
    if (!window.confirm("Regenerate the T+L brief link? The old link will stop working immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/matchmaking/brief-token/rotate");
      setToken(data.token);
      toast.success("New brief link generated. Share the updated URL.");
    } catch { toast.error("Could not regenerate link."); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied to clipboard.");
    } catch { toast.error("Copy failed — please select and copy the URL manually."); }
  };
  return (
    <div className="card-flat p-6" data-testid="prime-brief-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="overline">Travel + Leisure Matchmaking Brief</div>
          <h3 className="serif text-2xl mt-2">Shareable link for T+L sourcing leadership</h3>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
            A private URL showing every registered Prime Supplier&apos;s profile
            and matchmaking interests. Send it to Travel + Leisure so they can
            invite the right SBEs to Business Matchmaking Opportunities.
            No login required — the link works from any browser.
          </p>
        </div>
        <a href={publicUrl || "#"} target="_blank" rel="noreferrer"
           className="text-xs uppercase tracking-[0.18em] px-4 h-10 border border-zinc-300 hover:border-black inline-flex items-center"
           data-testid="prime-brief-preview">
          Preview brief
        </a>
      </div>
      <div className="mt-5 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-stretch">
        <input readOnly value={publicUrl}
               data-testid="prime-brief-url"
               className="h-11 px-3 border border-zinc-300 bg-zinc-50 text-xs font-mono truncate focus:outline-none focus:border-black" />
        <button onClick={copy} disabled={!token}
                className="h-11 px-4 text-xs uppercase tracking-[0.18em] bg-black text-white hover:bg-zinc-800 disabled:opacity-40"
                data-testid="prime-brief-copy">
          Copy link
        </button>
        <button onClick={rotate} disabled={busy}
                className="h-11 px-4 text-xs uppercase tracking-[0.18em] border border-zinc-300 hover:border-red-500 hover:text-red-600"
                data-testid="prime-brief-rotate">
          {busy ? "…" : "Regenerate"}
        </button>
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-zinc-400">
        Confidential · treat like any other private procurement document
      </div>
    </div>
  );
}


/* Data hygiene — one-click bulk actions for known-good cleanup ops
   (corrupted supplier logos, cancelled suppliers). */
function DataHygieneCard() {
  const [busy, setBusy] = useState(null); // "logos" | "remove" | null
  const [lastResult, setLastResult] = useState(null);
  const runFixLogos = async () => {
    if (!window.confirm("Replace the 5 corrupted logos (CDW, Pittsburgh Paints, Sobel Westex, Pellera, Ferguson) with cleaned transparent PNGs?")) return;
    setBusy("logos"); setLastResult(null);
    try {
      const { data } = await api.post("/admin/exhibitors/fix-corrupted-logos");
      setLastResult({ kind: "logos", items: data.results });
      const swapped = data.results.filter((r) => r.status === "swapped").length;
      toast.success(`Swapped ${swapped} of ${data.results.length} logos.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Logo fix failed."); }
    finally { setBusy(null); }
  };
  const runRemoveCancelled = async () => {
    if (!window.confirm("Permanently remove ODP Business Solutions and First Advantage? This will also delete any matchmaking interests and appointments for these suppliers.")) return;
    setBusy("remove"); setLastResult(null);
    try {
      const { data } = await api.post("/admin/exhibitors/remove-cancelled");
      setLastResult({ kind: "remove", items: data.removed });
      toast.success(`Removed ${data.removed.length} supplier record${data.removed.length === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Removal failed."); }
    finally { setBusy(null); }
  };
  return (
    <div className="card-flat p-6" data-testid="data-hygiene-card">
      <div className="overline">Data Hygiene</div>
      <h3 className="serif text-2xl mt-2">One-click cleanup actions</h3>
      <p className="text-sm text-zinc-600 mt-2 leading-relaxed max-w-2xl">
        Idempotent bulk actions maintained by Display America. Each button
        acts only on a pre-approved shortlist and is safe to re-run.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div className="border border-zinc-200 p-4">
          <div className="text-sm font-medium">Fix corrupted logos</div>
          <div className="text-xs text-zinc-500 mt-1">
            CDW · Pittsburgh Paints · Sobel Westex · Pellera · Ferguson.
            Replaces each with a cleaned transparent PNG.
          </div>
          <button onClick={runFixLogos} disabled={busy !== null}
                  className="mt-3 h-10 px-4 text-xs uppercase tracking-[0.18em] bg-black text-white hover:bg-zinc-800 disabled:opacity-40 w-full"
                  data-testid="fix-logos-btn">
            {busy === "logos" ? "Fixing…" : "Fix 5 logos"}
          </button>
        </div>
        <div className="border border-zinc-200 p-4">
          <div className="text-sm font-medium">Remove cancelled suppliers</div>
          <div className="text-xs text-zinc-500 mt-1">
            ODP Business Solutions · First Advantage. Removes the records and
            any related matchmaking interests / appointments.
          </div>
          <button onClick={runRemoveCancelled} disabled={busy !== null}
                  className="mt-3 h-10 px-4 text-xs uppercase tracking-[0.18em] border border-zinc-300 hover:border-red-500 hover:text-red-600 disabled:opacity-40 w-full"
                  data-testid="remove-cancelled-btn">
            {busy === "remove" ? "Removing…" : "Remove 2 cancelled"}
          </button>
        </div>
      </div>
      {lastResult && (
        <div className="mt-4 border-l-2 border-[#A39171] pl-4 text-xs" data-testid="hygiene-result">
          <div className="overline mb-1">Last run</div>
          {lastResult.items.length === 0 && <div className="text-zinc-500">Nothing matched.</div>}
          <ul className="grid gap-1">
            {lastResult.items.map((r, i) => (
              <li key={i} className="text-zinc-700">
                {r.company || r.match} — <span className="uppercase tracking-[0.14em] text-[10px] text-zinc-500">{r.status || "removed"}</span>
                {r.error && <span className="text-red-600"> · {r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


/* ============================================================
   Fireside Panelists Editor
   ============================================================ */
export function FiresidePanelistsTab() {
  const [panelists, setPanelists] = useState([]);
  useEffect(() => { api.get("/event").then(({ data }) => setPanelists(data.fireside_panelists || [])); }, []);
  return (
    <div className="grid gap-4" data-testid="fireside-tab">
      <div className="card-flat p-6">
        <div className="overline mb-2"><span className="accent-rule" />Executive Procurement Forum</div>
        <h3 className="serif text-2xl">Fireside Chat Panelists</h3>
        <p className="text-sm text-zinc-500 mt-2">Manage the three panelist slots. Edits appear immediately on the public Schedule page.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {panelists.map((p) => (
          <PanelistCard key={p.id} panelist={p}
            onSaved={(updated) => setPanelists((all) => all.map((x) => x.id === updated.id ? updated : x))} />
        ))}
      </div>
    </div>
  );
}

function PanelistCard({ panelist, onSaved }) {
  const [form, setForm] = useState({
    name: panelist.name || "", title: panelist.title || "", org: panelist.org || "",
    bio: panelist.bio || "", confirmed: !!panelist.confirmed,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/fireside/panelists/${panelist.id}`, form);
      const updated = data.fireside_panelists.find((x) => x.id === panelist.id);
      if (updated) onSaved(updated);
      toast.success("Panelist updated.");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-flat p-5 grid gap-3" data-testid={`panelist-card-${panelist.id}`}>
      <div className="flex items-center justify-between">
        <div className="overline">Slot {panelist.order}</div>
        <span className={`text-[10px] uppercase tracking-[0.18em] ${form.confirmed ? "text-emerald-700" : "text-amber-700"}`}>
          {form.confirmed ? "Confirmed" : "Pending"}
        </span>
      </div>
      <div><Label className="overline">Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-none mt-2" data-testid={`panelist-name-${panelist.id}`} /></div>
      <div><Label className="overline">Title</Label>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-none mt-2" /></div>
      <div><Label className="overline">Organization</Label>
        <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} className="h-11 rounded-none mt-2" /></div>
      <div><Label className="overline">Bio</Label>
        <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-none mt-2" /></div>
      <label className="text-xs inline-flex items-center gap-2 mt-1">
        <input type="checkbox" checked={form.confirmed} onChange={(e) => setForm({ ...form, confirmed: e.target.checked })} />
        Confirmed panelist
      </label>
      <button disabled={saving} onClick={save} className="btn-black h-10 text-xs" data-testid={`panelist-save-${panelist.id}`}>
        {saving ? "Saving…" : "Save panelist"}
      </button>
    </div>
  );
}





/* ============================================================
   Executive Updates Editor (homepage announcement stream)
   ============================================================ */
export function ExecutiveUpdatesTab() {
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", priority: "normal", active: true, order: 0 });
  useEffect(() => { api.get("/admin/executive-updates").then(({ data }) => setUpdates(data.updates || [])); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required.");
    try {
      const { data } = await api.post("/admin/executive-updates", form);
      setUpdates((u) => [...u, data.update].sort((a,b) => (a.order||0) - (b.order||0)));
      setForm({ title: "", body: "", priority: "normal", active: true, order: 0 });
      toast.success("Executive update added.");
    } catch (er) { toast.error("Failed"); }
  };
  const update = async (id, patch) => {
    try {
      const { data } = await api.put(`/admin/executive-updates/${id}`, patch);
      setUpdates((u) => u.map((x) => x.id === id ? data.update : x));
      toast.success("Updated.");
    } catch (e) { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try { await api.delete(`/admin/executive-updates/${id}`); setUpdates((u) => u.filter((x) => x.id !== id)); }
    catch (e) { toast.error("Delete failed"); }
  };
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6" data-testid="executive-updates-tab">
      <form onSubmit={add} className="card-flat p-6 grid gap-4">
        <div className="overline">Add Executive Update</div>
        <Input placeholder="Title (e.g. 'Brad Singh confirmed for Executive Procurement Forum')" required
          value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="h-11 rounded-none" data-testid="update-title" />
        <Textarea rows={2} placeholder="Body (optional)"
          value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
          className="rounded-none" />
        <div className="grid grid-cols-2 gap-3">
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="h-11 rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" placeholder="Order" value={form.order}
            onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
            className="h-11 rounded-none" />
        </div>
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active on homepage
        </label>
        <button className="btn-black" data-testid="update-add"><Plus size={14} /> Add Update</button>
      </form>
      <div className="grid gap-3">
        {updates.length === 0 && <div className="card-flat p-6 text-sm text-zinc-500">No executive updates.</div>}
        {updates.map((u) => (
          <div key={u.id} className={`card-flat p-4 ${u.active ? "" : "opacity-60"}`} data-testid={`update-row-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{u.title}</div>
                {u.body && <div className="text-xs text-zinc-500 mt-1">{u.body}</div>}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={`pill ${u.priority === "high" ? "bg-[#A39171] text-white border-[#A39171]" : ""}`}>{u.priority}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Order {u.order}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => update(u.id, { active: !u.active })}
                  className="text-xs uppercase tracking-[0.14em] px-3 h-8 border border-zinc-300 hover:border-black">
                  {u.active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => remove(u.id)} className="text-zinc-400 hover:text-black text-xs">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ============================================================
   Homepage Content Editor — Executive Quote, Featured Speaker, Featured Prime Supplier
   ============================================================ */
export function HomepageContentTab({ exhibitors }) {
  const [cfg, setCfg] = useState({ executive_quote: "", featured_speaker_id: "", featured_supplier_id: "" });
  const [speakers, setSpeakers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/event").then(({ data }) => setCfg({
      executive_quote: data.executive_quote || "",
      featured_speaker_id: data.featured_speaker_id || "",
      featured_supplier_id: data.featured_supplier_id || "",
    }));
    api.get("/speakers").then(({ data }) => setSpeakers(data.speakers || []));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/event-config", {
        executive_quote: cfg.executive_quote || null,
        featured_speaker_id: cfg.featured_speaker_id || null,
        featured_supplier_id: cfg.featured_supplier_id || null,
      });
      toast.success("Homepage content saved.");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid gap-6 max-w-3xl" data-testid="homepage-content-tab">
      <div className="card-flat p-6">
        <div className="overline mb-2"><span className="accent-rule" />Homepage</div>
        <h3 className="serif text-2xl">Homepage Content</h3>
        <p className="text-sm text-zinc-500 mt-2">
          Manage the unattributed executive quote and rotate the featured speaker / Prime Supplier shown on the homepage.
          Leaving a selection blank shows a &quot;Coming Soon&quot; card.
        </p>
      </div>

      <div className="card-flat p-6 grid gap-4">
        <Label className="overline">Executive Quote (unattributed)</Label>
        <Textarea rows={4} value={cfg.executive_quote}
          onChange={(e) => setCfg({ ...cfg, executive_quote: e.target.value })}
          className="rounded-none" data-testid="homepage-quote" />
        <p className="text-xs text-zinc-500">Displayed on the homepage without attribution until approved leadership content is confirmed.</p>
      </div>

      <div className="card-flat p-6 grid gap-4">
        <Label className="overline">Featured Speaker</Label>
        <Select value={cfg.featured_speaker_id || "__none__"} onValueChange={(v) => setCfg({ ...cfg, featured_speaker_id: v === "__none__" ? "" : v })}>
          <SelectTrigger className="h-11 rounded-none" data-testid="homepage-featured-speaker"><SelectValue placeholder="— Coming Soon —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Show &quot;Coming Soon&quot; —</SelectItem>
            {speakers.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name} · {s.org}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {speakers.length === 0 && <p className="text-xs text-amber-700">No speakers in the system yet. Add speakers on the Speakers page.</p>}
      </div>

      <div className="card-flat p-6 grid gap-4">
        <Label className="overline">Featured Prime Supplier</Label>
        <Select value={cfg.featured_supplier_id || "__none__"} onValueChange={(v) => setCfg({ ...cfg, featured_supplier_id: v === "__none__" ? "" : v })}>
          <SelectTrigger className="h-11 rounded-none" data-testid="homepage-featured-supplier"><SelectValue placeholder="— Auto-rotate approved suppliers —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Auto-rotate approved suppliers —</SelectItem>
            {exhibitors.filter((e) => e.status === "approved" && e.payment_status === "paid").map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-zinc-500">Only approved &amp; paid Prime Suppliers appear here. Leave blank to rotate automatically.</p>
      </div>

      <div className="flex justify-end">
        <button disabled={saving} onClick={save} className="btn-black" data-testid="homepage-save">
          {saving ? "Saving…" : "Save homepage content"}
        </button>
      </div>
    </div>
  );
}



/* ============================================================
   Production Stage inline editor — used in Prime Suppliers table.
   Admin advances a supplier through the 8-stage production pipeline.
   ============================================================ */
const PRODUCTION_STAGE_OPTIONS = [
  ["registered", "Registration Complete"],
  ["payment_received", "Payment Received"],
  ["logo_received", "Logo Received"],
  ["graphics_in_design", "Graphics In Design"],
  ["proof_ready", "Proof Ready"],
  ["approved", "Approved"],
  ["production_complete", "Production Complete"],
  ["ready_for_event", "Ready for Event"],
];

export function ProductionStageSelect({ exhibitor, onSaved }) {
  const [saving, setSaving] = useState(false);
  // Derive the current value: use override if set, otherwise infer from booleans
  const inferred = exhibitor.logo_file_id && exhibitor.payment_status === "paid" ? "logo_received"
                  : exhibitor.payment_status === "paid" ? "payment_received"
                  : "registered";
  const current = exhibitor.production_stage || inferred;
  const setStage = async (stage) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/exhibitors/${exhibitor.id}/production`, { production_stage: stage });
      onSaved(data.exhibitor);
      toast.success("Stage updated.");
    } catch (e) { toast.error("Update failed"); }
    finally { setSaving(false); }
  };
  return (
    <Select value={current} onValueChange={setStage} disabled={saving}>
      <SelectTrigger className="h-9 rounded-none min-w-[200px]" data-testid={`prod-stage-${exhibitor.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PRODUCTION_STAGE_OPTIONS.map(([k, label]) => (
          <SelectItem key={k} value={k}>{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


export function ProofControl({ exhibitor, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [editingRcpt, setEditingRcpt] = useState(false);
  const [rcptName, setRcptName] = useState(exhibitor.proof_recipient_name || "");
  const [rcptEmail, setRcptEmail] = useState(exhibitor.proof_recipient_email || "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newType, setNewType] = useState("graphics");
  const [newTitle, setNewTitle] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const proofs = Array.isArray(exhibitor.proofs) ? exhibitor.proofs : [];
  const rollup = exhibitor.proof_status; // ready | approved | revision_requested | undefined
  const primaryEmail = exhibitor.email;
  const activeRecipient = (exhibitor.proof_recipient_email || primaryEmail || "").trim();
  const activeName = exhibitor.proof_recipient_name || exhibitor.primary_contact || "";
  const usingAlternate = !!(exhibitor.proof_recipient_email && exhibitor.proof_recipient_email !== primaryEmail);

  const saveRecipient = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/exhibitors/${exhibitor.id}/proof-recipient`,
        { recipient_name: rcptName.trim(), recipient_email: rcptEmail.trim() || null });
      onSaved(data.exhibitor);
      toast.success(rcptEmail.trim() ? `Proofs will now go to ${rcptEmail.trim()}.`
                                     : "Reverted to the primary contact.");
      setEditingRcpt(false);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not save recipient.");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const displayType = newType === "other" ? (newTitle.trim() || "Proof") : proofTypeLabel(newType);
    const confirmMsg = usingAlternate
      ? `Send this ${displayType} proof to ${activeName || "the alternate approver"} at ${activeRecipient}?`
      : `Send this ${displayType} proof to the primary contact ${activeRecipient}?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const { data: up } = await api.post("/uploads", {
        filename: file.name, content_type: file.type || "application/pdf",
        data_base64: b64, purpose: "proof",
      });
      const { data } = await api.post(`/admin/exhibitors/${exhibitor.id}/proof`, {
        file_id: up.file_id,
        proof_type: newType,
        title: newTitle.trim() || null,
        notes: newNotes.trim() || null,
      });
      onSaved(data.exhibitor);
      toast.success(`${displayType} proof sent to ${activeRecipient}.`);
      setUploadOpen(false); setNewTitle(""); setNewNotes(""); setNewType("graphics");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Upload failed.");
    } finally { setBusy(false); }
  };

  const resendOne = async (proofId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/exhibitors/${exhibitor.id}/proofs/${proofId}/resend-email`, {});
      toast.success(`Proof email re-sent to ${data.sent_to}.`);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Resend failed.");
    } finally { setBusy(false); }
  };

  const approveOne = async (proofId, title) => {
    if (!window.confirm(`Approve "${title}" on behalf of the supplier? Once approved this proof is locked.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/exhibitors/${exhibitor.id}/proofs/${proofId}/approve`);
      onSaved(data.exhibitor);
      toast.success(`${title} approved.`);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Approval failed.");
    } finally { setBusy(false); }
  };

  const reviseOne = async (proofId, title) => {
    const comment = window.prompt(`Request a revision on "${title}" — what needs to change?`);
    if (!comment || comment.trim().length < 5) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/exhibitors/${exhibitor.id}/proofs/${proofId}/revise`,
        { comment: comment.trim() });
      onSaved(data.exhibitor);
      toast.success(`Revision requested on ${title}.`);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not request revision.");
    } finally { setBusy(false); }
  };

  const deleteOne = async (proofId, title) => {
    if (!window.confirm(`Remove "${title}"? The supplier will lose access to review it.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/exhibitors/${exhibitor.id}/proofs/${proofId}`);
      onSaved(data.exhibitor);
      toast.success("Proof removed.");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not remove proof.");
    } finally { setBusy(false); }
  };

  const badgeClass = rollup === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : rollup === "revision_requested" ? "bg-amber-50 text-amber-700 border-amber-200"
    : rollup === "ready" ? "bg-[#A39171]/10 text-[#A39171] border-[#A39171]/40"
    : "bg-zinc-50 text-zinc-500 border-zinc-200";
  const badgeText = rollup === "approved" ? "All approved · Locked"
    : rollup === "revision_requested" ? "Revision requested"
    : rollup === "ready" ? `Awaiting supplier · ${proofs.length}`
    : "No proofs";

  return (
    <div className="grid gap-2 min-w-[300px]" data-testid={`admin-proof-${exhibitor.id}`}>
      <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-1 inline-flex items-center justify-center ${badgeClass}`}>
        {badgeText}
      </span>

      {/* List of existing proofs, each with its own controls */}
      {proofs.length > 0 && (
        <div className="grid gap-1.5" data-testid={`admin-proof-list-${exhibitor.id}`}>
          {proofs.map((p) => (
            <div key={p.id} className="border border-zinc-200 p-2 text-[10px] bg-white"
                 data-testid={`admin-proof-entry-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="uppercase tracking-[0.18em] text-zinc-500">{proofTypeLabel(p.proof_type)}</div>
                  <div className="text-zinc-800 truncate">{p.title}</div>
                  {p.file_id && (
                    <a href={`${BACKEND_URL}/api/files/${p.file_id}`} target="_blank" rel="noreferrer"
                       className="underline underline-offset-4 text-zinc-500 truncate block mt-0.5">
                      {p.display_label || p.filename || "View"}
                    </a>
                  )}
                </div>
                <ProofEntryStatusPill status={p.status} />
              </div>
              {p.status === "revision_requested" && p.revision_comment && (
                <div className="mt-2 border-l-2 border-amber-500 pl-2 text-zinc-700 bg-amber-50/40 py-1">
                  <div className="uppercase tracking-[0.18em] text-[9px] text-amber-700 mb-0.5">Revision requested</div>
                  <div className="whitespace-pre-wrap">{p.revision_comment}</div>
                </div>
              )}
              {(p.supplier_notes || []).length > 0 && (
                <div className="mt-2 border-l-2 border-[#A39171] pl-2 grid gap-1.5 bg-zinc-50/60 py-1"
                     data-testid={`admin-proof-comments-${p.id}`}>
                  <div className="uppercase tracking-[0.18em] text-[9px] text-[#A39171]">
                    Supplier comment{p.supplier_notes.length !== 1 ? "s" : ""} ({p.supplier_notes.length})
                  </div>
                  {p.supplier_notes.map((n) => (
                    <div key={n.id} className="text-zinc-700">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-400">
                        {n.author || "Approver"}{n.at ? ` · ${new Date(n.at).toLocaleString([], {month:"short",day:"numeric",hour:"numeric",minute:"2-digit"})}` : ""}
                      </div>
                      <div className="whitespace-pre-wrap">{n.comment}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-1.5 flex gap-1 flex-wrap">
                {p.status === "ready" && (
                  <>
                    <button onClick={() => approveOne(p.id, p.title)} disabled={busy}
                            className="px-2 py-0.5 uppercase tracking-[0.16em] border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`admin-proof-approve-${p.id}`}>
                      Approve
                    </button>
                    <button onClick={() => reviseOne(p.id, p.title)} disabled={busy}
                            className="px-2 py-0.5 uppercase tracking-[0.16em] border border-amber-500 text-amber-700 hover:bg-amber-50"
                            data-testid={`admin-proof-revise-${p.id}`}>
                      Request revision
                    </button>
                  </>
                )}
                <button onClick={() => resendOne(p.id)} disabled={busy}
                        className="px-2 py-0.5 uppercase tracking-[0.16em] border border-zinc-300 hover:border-black"
                        data-testid={`admin-proof-resend-${p.id}`}>
                  Resend
                </button>
                {p.status !== "approved" && (
                  <button onClick={() => deleteOne(p.id, p.title)} disabled={busy}
                          className="px-2 py-0.5 uppercase tracking-[0.16em] border border-zinc-300 hover:border-red-500 hover:text-red-600"
                          data-testid={`admin-proof-delete-${p.id}`}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recipient panel */}
      <div className="border border-zinc-200 p-2 bg-zinc-50/60 text-[10px]" data-testid={`admin-proof-recipient-${exhibitor.id}`}>
        {!editingRcpt ? (
          <>
            <div className="uppercase tracking-[0.18em] text-zinc-500">
              Proof recipient {usingAlternate && <span className="text-[#A39171]">· alternate</span>}
            </div>
            <div className="mt-1 text-zinc-700 truncate" title={activeRecipient}>
              {activeName && <span className="font-medium">{activeName} · </span>}
              {activeRecipient || <span className="italic text-zinc-400">no email on file</span>}
            </div>
            <button onClick={() => setEditingRcpt(true)}
                    className="mt-1 underline underline-offset-4 text-zinc-500 hover:text-black"
                    data-testid={`admin-proof-recipient-edit-${exhibitor.id}`}>
              {usingAlternate ? "Change / clear" : "Send proofs to someone else…"}
            </button>
          </>
        ) : (
          <div className="grid gap-1">
            <input type="text" placeholder="Approver name"
                   value={rcptName} onChange={(e) => setRcptName(e.target.value)}
                   data-testid={`admin-proof-recipient-name-${exhibitor.id}`}
                   className="h-7 px-2 border border-zinc-300 rounded-none text-[11px] focus:outline-none focus:border-black" />
            <input type="email" placeholder="Approver email (blank = primary contact)"
                   value={rcptEmail} onChange={(e) => setRcptEmail(e.target.value)}
                   data-testid={`admin-proof-recipient-email-${exhibitor.id}`}
                   className="h-7 px-2 border border-zinc-300 rounded-none text-[11px] focus:outline-none focus:border-black" />
            <div className="flex gap-1">
              <button onClick={saveRecipient} disabled={busy}
                      className="flex-1 h-7 text-[10px] uppercase tracking-[0.16em] bg-black text-white hover:bg-zinc-700"
                      data-testid={`admin-proof-recipient-save-${exhibitor.id}`}>
                {busy ? "…" : "Save"}
              </button>
              <button onClick={() => { setEditingRcpt(false); setRcptName(exhibitor.proof_recipient_name || ""); setRcptEmail(exhibitor.proof_recipient_email || ""); }}
                      className="h-7 px-2 text-[10px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black"
                      data-testid={`admin-proof-recipient-cancel-${exhibitor.id}`}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload new proof (adds to the list, doesn't replace) */}
      {!uploadOpen && (
        <button onClick={() => setUploadOpen(true)}
                className="h-8 text-[10px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black inline-flex items-center justify-center gap-1"
                data-testid={`admin-proof-upload-open-${exhibitor.id}`}>
          <Upload size={10} /> Add proof
        </button>
      )}
      {uploadOpen && (
        <div className="border border-zinc-300 p-2 grid gap-1.5 text-[10px] bg-white"
             data-testid={`admin-proof-upload-panel-${exhibitor.id}`}>
          <div>
            <label className="uppercase tracking-[0.18em] text-zinc-500">Proof type</label>
            <select value={newType} onChange={(e) => setNewType(e.target.value)}
                    data-testid={`admin-proof-type-${exhibitor.id}`}
                    className="w-full h-7 mt-0.5 border border-zinc-300 rounded-none text-[11px] focus:outline-none focus:border-black">
              <option value="graphics">Graphics</option>
              <option value="rendering">Color Rendering</option>
              <option value="other">Other…</option>
            </select>
          </div>
          <input type="text"
                 placeholder={newType === "other" ? "Custom label (required)" : "Custom title (optional)"}
                 value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                 data-testid={`admin-proof-title-${exhibitor.id}`}
                 className="h-7 px-2 border border-zinc-300 rounded-none text-[11px] focus:outline-none focus:border-black" />
          <textarea rows={2} placeholder="Admin notes (optional, visible to supplier)"
                    value={newNotes} onChange={(e) => setNewNotes(e.target.value)}
                    data-testid={`admin-proof-newnotes-${exhibitor.id}`}
                    className="w-full px-2 py-1 border border-zinc-300 rounded-none text-[11px] focus:outline-none focus:border-black" />
          <label className={`h-8 text-[10px] uppercase tracking-[0.16em] border border-zinc-300 inline-flex items-center justify-center gap-1 cursor-pointer ${busy || (newType === "other" && !newTitle.trim()) ? "opacity-40 pointer-events-none" : "hover:border-black"}`}
                 data-testid={`admin-proof-upload-file-${exhibitor.id}`}>
            <Upload size={10} /> {busy ? "…" : "Upload & send"}
            <input type="file" hidden accept="application/pdf,image/*"
                   onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          <button onClick={() => { setUploadOpen(false); setNewTitle(""); setNewNotes(""); setNewType("graphics"); }}
                  className="h-7 text-[10px] uppercase tracking-[0.16em] border border-zinc-300 hover:border-black"
                  data-testid={`admin-proof-upload-cancel-${exhibitor.id}`}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function proofTypeLabel(t) {
  const s = (t || "").toLowerCase();
  if (s === "graphics") return "Graphics";
  if (s === "rendering" || s === "color_rendering") return "Color Rendering";
  if (s === "signage") return "Signage";
  if (!t) return "Proof";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function ProofEntryStatusPill({ status }) {
  const cls = status === "approved" ? "border-emerald-500 text-emerald-700 bg-emerald-50"
    : status === "revision_requested" ? "border-amber-500 text-amber-700 bg-amber-50"
    : "border-[#A39171] text-[#A39171] bg-[#A39171]/10";
  const label = status === "approved" ? "Approved"
    : status === "revision_requested" ? "Revise"
    : "Ready";
  return (
    <span className={`text-[9px] uppercase tracking-[0.18em] border px-1.5 py-0.5 shrink-0 ${cls}`}>
      {label}
    </span>
  );
}

